import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * SiteUp — por que estes menus da sidebar são teleportados
 *
 * A sidebar tem `position: relative` + `z-index: 40` (`Sidebar.vue`), e isso
 * cria um CONTEXTO DE EMPILHAMENTO: qualquer coisa renderizada dentro dela
 * fica presa nesse contexto, por mais alto que seja o próprio z-index. Um
 * dropdown com `z-50` lá dentro continua atrás da lista de conversas — some
 * tudo que passa da largura da sidebar (com ela recolhida, sobra uma fatia
 * de ~56px e o menu fica ilegível).
 *
 * A saída é a mesma que o `SidebarCollapsedPopover.vue` já usava: teleportar
 * pro <body> e posicionar à mão a partir do retângulo do gatilho.
 *
 * @param {'top'|'bottom'} placement 'top' abre pra cima (rodapé da sidebar),
 *   'bottom' abre pra baixo (topo da sidebar).
 * @param {number} gap distância em px entre o gatilho e o menu.
 */
export function useSidebarDropdownAnchor({ placement = 'top', gap = 8 } = {}) {
  const isRTL = useMapGetter('accounts/isRTL');

  const triggerRef = ref(null);
  const anchorStyle = ref({});
  // Teto de altura pro menu nunca passar da tela numa janela baixa — sem isto
  // o último item (ex.: "Encerrar sessão") ficaria inalcançável.
  const menuMaxHeight = ref(undefined);

  const EDGE = 8;

  const measure = () => {
    const el = triggerRef.value;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const side = isRTL.value ? 'right' : 'left';
    const inset = isRTL.value
      ? window.innerWidth - rect.right
      : Math.max(0, rect.left);

    const available =
      placement === 'top'
        ? rect.top - gap - EDGE
        : window.innerHeight - rect.bottom - gap - EDGE;

    menuMaxHeight.value = `${Math.max(160, Math.round(available))}px`;

    anchorStyle.value =
      placement === 'top'
        ? {
            bottom: `${Math.round(window.innerHeight - rect.top + gap)}px`,
            [side]: `${Math.round(inset)}px`,
          }
        : {
            top: `${Math.round(rect.bottom + gap)}px`,
            [side]: `${Math.round(inset)}px`,
          };
  };

  // Redimensionar a janela move o gatilho; sem isso o menu aberto ficaria
  // ancorado na posição antiga.
  onMounted(() => window.addEventListener('resize', measure));
  onBeforeUnmount(() => window.removeEventListener('resize', measure));

  return { triggerRef, anchorStyle, menuMaxHeight, measure };
}
